import moment from 'moment';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { ButtonUI, Input, useQueryFetch } from 'sonata-react-components';

import { getCountryLabel } from '../../MultiFlightMap.helpers';
import { useMapPopover } from '../MapPopover/MapPopover.context';
import { resolveCountryCca3 } from '../MapView/map.helpers';

import './shapeConfirmation.scss';

type Props = {
  center: [number, number];
  onCancel: () => void;
  onConfirm: (data: { name: string }) => void;
};

type State = {
  defaultName: string;
  name: string;
};

export function PolygonConfirmation({ center, onCancel, onConfirm }: Props) {
  const { formatMessage } = useIntl();
  const { closeMapPopover } = useMapPopover();

  const [state, setState] = useState<State>(() => {
    const defaultName = `New area ${moment().format('YYYY-MM-DD hh:mm:ss')}`;
    return { defaultName, name: defaultName };
  });

  const { data: country, isLoading: isResolvingCountry } = useQueryFetch<string>(
    ['multiFlightMap.shapeConfirmation.country', center[0], center[1]],
    () => resolveCountryCca3(center[1], center[0]),
    { staleTime: Number.POSITIVE_INFINITY }
  );

  const handleNameChange = (value: string | FileList | null) => {
    if (typeof value === 'string') {
      setState(prev => ({ ...prev, name: value }));
    }
  };

  const handleConfirm = () => {
    const trimmedName = state.name.trim() || state.defaultName;
    closeMapPopover();
    onConfirm({ name: trimmedName });
  };

  const handleCancel = () => {
    closeMapPopover();
    onCancel();
  };

  const countryLabel = isResolvingCountry
    ? formatMessage({
        defaultMessage: 'Detecting…',
        id: 'multiFlightLocations.confirmation.country.loading'
      })
    : country
      ? getCountryLabel(country)
      : formatMessage({
          defaultMessage: 'N/A',
          id: 'common.notAvailableShort'
        });

  return (
    <div className="shapeConfirmation">
      <Input
        className="shapeConfirmation__input"
        defaultValue={state.name}
        id="polygonConfirmation-name"
        label={formatMessage({
          defaultMessage: 'Name',
          id: 'common.name'
        })}
        name="polygonConfirmation-name"
        notFloatingPlaceholder
        onChange={handleNameChange}
        shape="squareUI"
        tiny
        type="text"
      />
      <div className="shapeConfirmation__row">
        <span className="shapeConfirmation__label">
          {formatMessage({
            defaultMessage: 'Country:',
            id: 'mapAreaConfirmation.country'
          })}
        </span>
        <span className="shapeConfirmation__value">{countryLabel}</span>
      </div>
      <div className="shapeConfirmation__buttonsContainer">
        <ButtonUI
          icon="fal fa-xmark"
          onClick={handleCancel}
          size="ultraSmall"
          text={formatMessage({
            defaultMessage: 'Cancel',
            id: 'common.cancel'
          })}
          type="tertiary"
        />
        <ButtonUI
          className="shapeConfirmation__acceptButton"
          icon="fa-regular fa-floppy-disk"
          onClick={handleConfirm}
          size="ultraSmall"
          text={formatMessage({
            defaultMessage: 'Add Area',
            id: 'mapAreaConfirmation.addButton'
          })}
          type="primary"
        />
      </div>
    </div>
  );
}
